import { formatCurrency } from '@angular/common';
import { Injectable } from '@angular/core';
import { Order, OrderHeader, OrderItem } from '@usf/ngrx-order';
import { Partner } from '@usf/partner-types';
import { Product } from '@usf/product-types';
import { SiteCustomizationProfile } from '@usf/user-types/site-customization';
import dayjs from 'dayjs';
import utc from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

@Injectable({
  providedIn: 'root',
})
export class OrderCXMLDtoService {
  constructor() {}

  static orderToCXMLFormat(
    orders: Order[],
    productMap: Map<number, Product>,
    partner: Partner,
    buyerCookie: string,
    browserFormPost: string,
    userId: string,
    siteCustomization?: SiteCustomizationProfile,
  ) {
    const dt = document.implementation.createDocumentType(
      'cXML',
      '',
      'http://xml.cxml.org/schemas/cXML/1.2.033/cXML.dtd',
    );
    const xml = document.implementation.createDocument(
      '',
      '',
      dt,
    ) as XMLDocument;

    const root = xml.createElement('cXML');
    let headerEle: HTMLElement;

    if (siteCustomization?.profileName === 'BROOKDALE') {
      headerEle = this.createBrookdaleHeader(xml, buyerCookie, browserFormPost);
    } else {
      root.setAttribute('payloadID', buyerCookie);
      root.setAttribute('timestamp', dayjs().format());
      root.setAttribute('xml:lang', 'enUS');
      headerEle = this.createGenericHeader(xml, partner, userId);
    }

    //Header creation
    //PunchOutOrderMessage Creation
    const messageEle = xml.createElement('Message');
    messageEle.setAttribute('deploymentMode', 'production');
    const punchOutOrderMessageEle = xml.createElement('PunchOutOrderMessage');

    punchOutOrderMessageEle.appendChild(
      this.createXMLElement(xml, 'BuyerCookie', buyerCookie),
    );

    //PunchOutOrderMessageHeader
    const orderMessageHeaderEle = xml.createElement(
      'PunchOutOrderMessageHeader',
    );
    orderMessageHeaderEle.setAttribute('operationAllowed', 'create');
    orderMessageHeaderEle.setAttribute('quoteStatus', 'final');

    orderMessageHeaderEle.appendChild(
      this.createCurrencyElement(xml, 'Total', null),
    );
    punchOutOrderMessageEle.appendChild(orderMessageHeaderEle);
    //Substituted products
    const substitutedProducts = new Set<number>();

    orders.forEach(order =>
      order.orderItems
        .filter(item => !!item.substituteFor)
        .forEach(item => substitutedProducts.add(item.substituteFor)),
    );

    for (const order of orders) {
      for (const item of order.orderItems) {
        this.generateItemElement(
          xml,
          punchOutOrderMessageEle,
          order.orderHeader,
          item,
          productMap,
          substitutedProducts,
          partner,
        );
      }
    }
    //Build Header
    root.appendChild(headerEle);

    //Message
    messageEle.appendChild(punchOutOrderMessageEle);
    root.appendChild(messageEle);

    xml.appendChild(root);
    return new XMLSerializer().serializeToString(xml);
  }

  static generateItemElement(
    xml: XMLDocument,
    messageEle: HTMLElement,
    orderHeader: OrderHeader,
    item: OrderItem,
    productMap: Map<number, Product>,
    substitutedProducts: Set<number>,
    partner: Partner,
  ) {
    const itemEle = xml.createElement('ItemIn');
    const product = productMap.get(item.productNumber);
    let itemQuantity = 0;

    //Check if delivery date is within substitution threshold
    const today = new Date();
    const deliveryMax = new Date();
    deliveryMax.setDate(today.getDate() + 2);

    const isWithinDeliveryWindow =
      new Date(orderHeader.confirmedDeliveryDate) <= deliveryMax;

    //Total Item Quantity Calculation
    const units =
      (!!item?.substituteFlag ||
        substitutedProducts.has(item?.productNumber)) &&
      !partner.punchoutProfile.orderCountFlag &&
      isWithinDeliveryWindow
        ? item.unitsReserved
        : (item?.unitsOrdered?.currentValue ?? 0);
    const eaches =
      (!!item?.substituteFlag ||
        substitutedProducts.has(item?.productNumber)) &&
      !partner.punchoutProfile.orderCountFlag &&
      isWithinDeliveryWindow
        ? item.eachesReserved
        : (item?.eachesOrdered?.currentValue ?? 0);

    let price = 0;

    if (
      product?.summary?.catchWeightFlag &&
      partner.punchoutProfile.catchWeight
    ) {
      const netWeight = product?.summary?.netWeight || 0;
      const totalCasesQuantity = netWeight * units;
      const totalEachesQuantity =
        (netWeight * eaches) / (product?.inventory?.eachConversionFactor || 1);
      itemQuantity = totalCasesQuantity + totalEachesQuantity;
      price = item.unitPrice;
    } else if (!product?.summary?.breakable) {
      itemQuantity = units;
      price = item.unitPrice;
    } else {
      if (units > 0 && eaches > 0) {
        //Generate a separate ItemIn element for unit and eaches item
        itemQuantity = units;
        price = item.unitPrice;
        //Remove units count to split item into two ItemInElements
        const eachesItem = {
          ...item,
          unitsOrdered: { ...item.unitsOrdered, currentValue: 0 },
          unitsReserved: 0,
        } as OrderItem;
        this.generateItemElement(
          xml,
          messageEle,
          orderHeader,
          eachesItem,
          productMap,
          substitutedProducts,
          partner,
        );
      } else if (units > 0) {
        itemQuantity = units;
        price = item.unitPrice;
      } else {
        itemQuantity = eaches;
        price = item.eachPrice;
      }
    }
    itemEle.setAttribute('quantity', Math.floor(itemQuantity).toString());

    //ItemID
    const itemIDEle = xml.createElement('ItemID');
    itemIDEle.appendChild(
      this.createXMLElement(
        xml,
        'SupplierPartID',
        item.productNumber?.toString(),
      ),
    );
    itemIDEle.appendChild(
      this.createXMLElement(
        xml,
        'SupplierPartAuxiliaryID',
        `${orderHeader?.tandemOrderNumber};${orderHeader?.divisionNumber};${orderHeader?.customerNumber};${orderHeader?.departmentNumber}`,
      ),
    );
    itemEle.appendChild(itemIDEle);

    //ItemDetail
    const itemDetailEle = xml.createElement('ItemDetail');
    itemDetailEle.appendChild(
      this.createCurrencyElement(xml, 'UnitPrice', price),
    );

    //Description
    itemDetailEle.appendChild(
      this.createXMLElement(
        xml,
        'Description',
        this.escapeXMLString(product?.summary?.productDescTxtl),
        { name: 'xml:lang', value: 'enUS' },
      ),
    );

    //UnitOfMeasure
    let UOM: string;
    if (units > 0 && item.priceUOM !== 'CS' && item.priceUOM !== 'CA') {
      UOM = item?.priceUOM;
    } else if (units > 0 && partner.punchoutProfile.casesEaches?.length > 0) {
      UOM = partner.punchoutProfile.casesEaches;
    } else if (units === 0 && eaches > 0) {
      UOM =
        product?.summary?.catchWeightFlag && product?.summary?.breakable
          ? product?.summary.priceUom
          : product?.summary?.eachUom;
    } else {
      UOM = item?.priceUOM;
    }
    itemDetailEle.appendChild(this.createXMLElement(xml, 'UnitOfMeasure', UOM));

    //PIM Category Code
    itemDetailEle.appendChild(
      this.createXMLElement(
        xml,
        'Classification',
        product?.summary?.categoryCode,
        { name: 'domain', value: 'UNSPSC' },
      ),
    );

    //Pack Size
    itemDetailEle.appendChild(
      this.createXMLElement(
        xml,
        'Extrinsic',
        this.escapeXMLString(product?.summary?.salesPackSize),
        { name: 'name', value: 'PackSize' },
      ),
    );

    //Delivery Date
    itemDetailEle.appendChild(
      this.createXMLElement(
        xml,
        'Extrinsic',
        dayjs.utc(orderHeader?.confirmedDeliveryDate).format('YYYY-MM-DD'),
        { name: 'name', value: 'DeliveryDate' },
      ),
    );

    itemEle.appendChild(itemDetailEle);
    messageEle.appendChild(itemEle);
    return messageEle;
  }

  static createGenericHeader(
    xml: XMLDocument,
    partner: Partner,
    userId: string,
  ) {
    const headerEle = xml.createElement('Header');

    //From
    const fromEle = xml.createElement('From');
    const fromCredentialEle = xml.createElement('Credential');
    fromCredentialEle.setAttribute('domain', 'DUNS');
    const fromIdentity = this.createXMLElement(xml, 'Identity', 'USFOODS');
    fromCredentialEle.appendChild(fromIdentity);
    fromEle.appendChild(fromCredentialEle);

    //To
    const toEle = xml.createElement('To');
    const toCredentialEle = xml.createElement('Credential');
    toCredentialEle.setAttribute('domain', 'DUNS');

    toCredentialEle.appendChild(this.createXMLElement(xml, 'Identity', userId));
    toEle.appendChild(toCredentialEle);

    //Sender
    const senderEle = xml.createElement('Sender');
    const senderCredentialEle = xml.createElement('Credential');
    senderCredentialEle.setAttribute('domain', 'DUNS');
    senderCredentialEle.appendChild(
      this.createXMLElement(xml, 'Identity', 'USFOODS'),
    );
    senderEle.appendChild(senderCredentialEle);
    senderEle.appendChild(this.createXMLElement(xml, 'UserAgent', 'USFOODS'));
    headerEle.appendChild(fromEle);
    headerEle.appendChild(toEle);
    headerEle.appendChild(senderEle);
    return headerEle;
  }

  static createBrookdaleHeader(
    xml: XMLDocument,
    buyerCookie: string,
    browserFormPost: string,
  ) {
    //need more
    const headerEle = xml.createElement('header');
    headerEle.appendChild(
      this.createXMLElement(xml, 'browserFormPost', browserFormPost),
    );
    headerEle.appendChild(this.createXMLElement(xml, 'payloadID', buyerCookie));

    headerEle.appendChild(
      this.createXMLElement(xml, 'timeStamp', dayjs().format()),
    );

    headerEle.appendChild(this.createXMLElement(xml, 'language', 'enUS'));
    return headerEle;
  }

  static createXMLElement(
    doc: XMLDocument,
    elementName: string,
    value: string,
    attribute?: { name: string; value: string },
  ) {
    const element = doc.createElement(elementName);
    element.innerHTML = value;
    if (!!attribute) {
      element.setAttribute(attribute.name, attribute.value);
    }
    doc.appendChild(element);
    return element;
  }

  static createCurrencyElement(
    doc: XMLDocument,
    elementName: string,
    value: number,
  ) {
    const element = doc.createElement(elementName);
    const moneyElement = doc.createElement('Money');
    moneyElement.setAttribute('currency', 'USD');
    moneyElement.innerHTML = !!value
      ? formatCurrency(value, 'en-US', '', 'USD', '1.2-2')
      : '';
    element.appendChild(moneyElement);
    doc.appendChild(element);
    return element;
  }

  static escapeXMLString(unsafe?: string) {
    return unsafe
      ?.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }
}
